// src/pages/Admin/Adherents/components/AdherentsTable.jsx

import React, { useMemo } from "react";
import { FaEdit, FaFilePdf, FaLayerGroup, FaTrash } from "react-icons/fa";
import { ALL_COLUMNS } from "../columns.config";
import {
  fallbackAdherentPhoto,
  formatDate,
  getAdherentId,
  getAdherentName,
  getAdherentPhoto,
  sortIcon,
} from "../helpers";

export default function AdherentsTable({
  rows,
  totalCount,
  visibleCount,
  loading,
  scrollRef,
  onScroll,
  visibleColumnIds,
  createdSortOrder,
  toggleCreatedSort,
  ctx,
  onEdit,
  onDelete,
  onPrint,
  onGroups,
}) {
  const visibleColumns = useMemo(() => {
    return ALL_COLUMNS.filter((column) => visibleColumnIds.includes(column.id));
  }, [visibleColumnIds]);

  const minWidth = useMemo(() => {
    const dynamic = visibleColumns.reduce((sum, column) => {
      return sum + Number(column.minWidth || 160);
    }, 0);

    return Math.max(1180, 320 + dynamic + 190);
  }, [visibleColumns]);

  return (
    <div className="p-3 sm:p-5 lg:p-6">
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="max-h-[72vh] overflow-auto rounded-[24px] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950/60"
      >
        <table className="w-full text-sm" style={{ minWidth }}>
          <thead className="sticky top-0 z-30 bg-slate-50 dark:bg-slate-900">
            <tr className="text-left">
              <th className="sticky left-0 z-40 w-[320px] bg-slate-50 px-4 py-3 font-bold text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                Adhérent
              </th>

              {visibleColumns.map((column) => (
                <th
                  key={column.id}
                  className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200"
                  style={{ minWidth: column.minWidth || 160 }}
                >
                  {column.id === "createdAt" ? (
                    <button
                      type="button"
                      onClick={toggleCreatedSort}
                      className="inline-flex items-center gap-2 rounded-xl px-2 py-1 transition hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      Créé le
                      <span>{sortIcon(createdSortOrder)}</span>
                    </button>
                  ) : column.renderHeader ? (
                    column.renderHeader(rows, ctx)
                  ) : (
                    column.label
                  )}
                </th>
              ))}

              <th className="sticky right-0 z-40 w-[190px] bg-slate-50 px-4 py-3 text-right font-bold text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {loading ? (
              [...Array(8)].map((_, index) => (
                <tr key={index}>
                  <td colSpan={visibleColumns.length + 2} className="px-4 py-3">
                    <div className="h-10 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />
                  </td>
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColumns.length + 2}
                  className="px-4 py-10 text-center text-slate-500 dark:text-slate-400"
                >
                  Aucun adhérent ne correspond aux filtres.
                </td>
              </tr>
            ) : (
              rows.map((adherent) => {
                const adherentId = getAdherentId(adherent);

                return (
                  <tr
                    key={adherentId}
                    className="transition hover:bg-slate-50 dark:hover:bg-slate-900"
                  >
                    <td className="sticky left-0 z-20 bg-white px-4 py-3 dark:bg-slate-950">
                      <div className="flex items-center gap-3">
                        <img
                          src={getAdherentPhoto(adherent)}
                          alt={getAdherentName(adherent)}
                          className="h-12 w-12 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                          onError={(event) => {
                            event.currentTarget.src = fallbackAdherentPhoto(adherent?.genre);
                          }}
                        />

                        <div className="min-w-0">
                          <div className="truncate font-bold text-slate-900 dark:text-white">
                            {getAdherentName(adherent)}
                          </div>

                          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                            <span>ID {adherentId}</span>
                            <span>•</span>
                            <span>{adherent?.licence_number || "Licence —"}</span>
                            <span>{adherent?.licence_number_jjb || "Licence —"}</span>
                            <span>•</span>
                            <span>{formatDate(adherent?.createdAt || adherent?.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    </td>

                    {visibleColumns.map((column) => (
                      <td key={column.id} className="px-4 py-3 align-middle">
                        {column.renderCell ? column.renderCell(adherent, ctx) : null}
                      </td>
                    ))}

                    <td className="sticky right-0 z-20 bg-white px-4 py-3 dark:bg-slate-950">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => onPrint(adherent)}
                          className="flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                          title="Fiche PDF"
                        >
                          <FaFilePdf />
                        </button>

                        <button
                          type="button"
                          onClick={() => onGroups(adherent)}
                          className="flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white text-emerald-600 transition hover:bg-emerald-50 dark:border-slate-700 dark:bg-slate-900 dark:text-emerald-300 dark:hover:bg-emerald-500/10"
                          title="Gérer les groupes"
                        >
                          <FaLayerGroup />
                        </button>

                        <button
                          type="button"
                          onClick={() => onEdit(adherent)}
                          className="flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                          title="Modifier"
                        >
                          <FaEdit />
                        </button>

                        <button
                          type="button"
                          onClick={() => onDelete(adherentId)}
                          className="flex h-9 w-9 items-center justify-center rounded-2xl bg-rose-600 text-white transition hover:bg-rose-700"
                          title="Supprimer"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {!loading && visibleCount < totalCount ? (
          <div className="border-t border-slate-200 p-3 text-center text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
            Affichage de {visibleCount} / {totalCount} — descendez pour charger la suite.
          </div>
        ) : null}
      </div>
    </div>
  );
}